import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Pencil } from 'lucide-react';
import { DataTable, type ColumnDef } from '@/shared/components/DataTable';
import type { EmailTemplate } from '../types/EmailTemplateTypes';

interface EmailTemplateListProps {
  templates: EmailTemplate[];
  userLanguage: string;
  onEdit: (template: EmailTemplate) => void;
}

export const EmailTemplateList = ({ templates, userLanguage, onEdit }: EmailTemplateListProps) => {
  const { t } = useTranslation();

  // Group by slug, prefer the user's language; fallback to first available
  const bySlug = new Map<string, EmailTemplate>();
  for (const tpl of templates) {
    const existing = bySlug.get(tpl.slug);
    if (!existing) {
      bySlug.set(tpl.slug, tpl);
    } else if (tpl.language === userLanguage && existing.language !== userLanguage) {
      bySlug.set(tpl.slug, tpl);
    }
  }
  const rows = Array.from(bySlug.values()).sort((a, b) => a.name.localeCompare(b.name));

  const columns: ColumnDef<EmailTemplate>[] = [
    {
      accessorFn: (row) => row.name,
      id: 'name',
      size: 240,
      header: t('settings.emailTemplates.columns.name'),
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium leading-tight">{row.original.name}</span>
          <span className="font-mono text-xs text-muted-foreground">{row.original.slug}</span>
        </div>
      ),
    },
    {
      accessorFn: (row) => row.subject,
      id: 'subject',
      size: 420,
      header: t('settings.emailTemplates.columns.subject'),
      cell: ({ row }) => (
        <span className="block truncate text-sm text-muted-foreground" title={row.original.subject}>
          {row.original.subject}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 48,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            title={t('settings.emailTemplates.edit')}
            onClick={() => onEdit(row.original)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      searchPlaceholder={t('settings.emailTemplates.searchPlaceholder')}
      noResultsText={t('settings.emailTemplates.noTemplates')}
      pagination
      defaultPageSize={10}
    />
  );
};
